/**
 * Calendar arithmetic on ISO date strings (KTD6, KTD10). Pure: no clock; `today` is a parameter.
 * `YYYY-MM-DD` strings compare correctly as strings, so ranges are checked with `<=`.
 */
import type { Calendar, CalendarBreak, IsoDate } from './types';

const DAY_MS = 86_400_000;
const ISO = /^\d{4}-\d{2}-\d{2}$/;

const toMs = (date: IsoDate) => Date.parse(`${date}T00:00:00Z`);
const fromMs = (ms: number): IsoDate => new Date(ms).toISOString().slice(0, 10);
const ymd = (year: number, month: number, day: number): IsoDate => fromMs(Date.UTC(year, month - 1, day));

export function isIsoDate(value: string): boolean {
  return ISO.test(value) && !Number.isNaN(toMs(value)) && fromMs(toMs(value)) === value;
}

export function addDays(date: IsoDate, days: number): IsoDate {
  return fromMs(toMs(date) + days * DAY_MS);
}

/** 0 is Sunday, 1 is Monday … 6 is Saturday. */
export function dayOfWeek(date: IsoDate): number {
  return new Date(toMs(date)).getUTCDay();
}

/** The Monday of the week `date` falls in. */
export function mondayOf(date: IsoDate): IsoDate {
  return addDays(date, -((dayOfWeek(date) + 6) % 7));
}

/** The school week a date belongs to: its own Monday on weekdays, the coming Monday on a weekend. */
export function weekOf(date: IsoDate): IsoDate {
  const dow = dayOfWeek(date);
  return addDays(mondayOf(date), dow === 0 || dow === 6 ? 7 : 0);
}

/** The ISO 8601 week a date falls in, as `2026-W42`: the year of its Thursday, weeks from Monday. */
export function isoWeek(date: IsoDate): string {
  const thursday = addDays(mondayOf(date), 3);
  const year = thursday.slice(0, 4);
  const week = Math.floor((toMs(thursday) - toMs(`${year}-01-01`)) / DAY_MS / 7) + 1;
  return `${year}-W${String(week).padStart(2, '0')}`;
}

const inRange = (date: IsoDate, start: IsoDate, end: IsoDate) => start <= date && date <= end;

/** Monday to Friday of the week from `monday`. */
export const weekDates = (monday: IsoDate): IsoDate[] => [0, 1, 2, 3, 4].map(n => addDays(monday, n));

/** The Monday-to-Friday days of a week that are in the year and outside every break. */
export function schoolDays(calendar: Calendar, monday: IsoDate): IsoDate[] {
  return weekDates(monday)
    .filter(d => inRange(d, calendar.firstDay, calendar.lastDay) && !calendar.breaks.some(b => inRange(d, b.start, b.end)));
}

/** Earliest and latest year a calendar may touch, and its longest span (KTD6). */
export const MIN_YEAR = 2000;
export const MAX_YEAR = 2100;
const MAX_SPAN_YEARS = 2;
/**
 * The most weeks `teachingWeeks` will walk: two years plus slack. `calendarProblems` already
 * refuses longer spans; this is the backstop for a caller that skipped it, since past year 9999
 * `addDays` yields `+010000-…`, which sorts before every four-digit year and never ends the loop.
 */
export const MAX_TEACHING_WEEKS = 110;

/** Mondays of the teaching weeks in order: weeks with at least one school day (KTD6). */
export function teachingWeeks(calendar: Calendar): IsoDate[] {
  const weeks: IsoDate[] = [];
  let monday = mondayOf(calendar.firstDay);
  for (let walked = 0; walked < MAX_TEACHING_WEEKS && monday <= calendar.lastDay; walked++, monday = addDays(monday, 7)) {
    if (schoolDays(calendar, monday).length > 0) weeks.push(monday);
  }
  return weeks;
}

/** The first break overlapping a week's weekdays, if any. */
export function breakIn(calendar: Calendar, monday: IsoDate): CalendarBreak | undefined {
  const friday = addDays(monday, 4);
  return calendar.breaks.find(b => b.start <= friday && monday <= b.end);
}

/** The nth given weekday (0 Sunday … 6 Saturday) of a month. */
const nthWeekday = (year: number, month: number, weekday: number, n: number) => {
  const first = ymd(year, month, 1);
  return addDays(first, ((weekday - dayOfWeek(first) + 7) % 7) + 7 * (n - 1));
};

/**
 * Typical dates for the school year starting in `year` (R4, plan Assumptions): the third Monday
 * of August to the last Friday of May, with Thanksgiving week, 21 December to 1 January, and the
 * second full week of March off.
 */
export function typicalCalendar(year: number): Calendar {
  const may31 = ymd(year + 1, 5, 31);
  // Thanksgiving is the fourth Thursday; the break is its Monday-to-Friday week.
  const thanksgiving = addDays(nthWeekday(year, 11, 4, 4), -3);
  // The first Monday of March always starts a full Monday-to-Friday week inside March.
  const spring = nthWeekday(year + 1, 3, 1, 2);
  return {
    firstDay: nthWeekday(year, 8, 1, 3),
    lastDay: addDays(may31, -((dayOfWeek(may31) + 2) % 7)),
    breaks: [
      { name: 'Thanksgiving', start: thanksgiving, end: addDays(thanksgiving, 4), kind: 'break' },
      { name: 'Winter break', start: ymd(year, 12, 21), end: ymd(year + 1, 1, 1), kind: 'break' },
      { name: 'Spring break', start: spring, end: addDays(spring, 4), kind: 'break' },
    ],
  };
}

/** Problems that make a calendar unusable, as stable codes; empty when it is usable. */
export function calendarProblems(calendar: Calendar): string[] {
  const problems: string[] = [];
  const firstOk = isIsoDate(calendar.firstDay);
  const lastOk = isIsoDate(calendar.lastDay);
  if (!firstOk) problems.push('first_day_invalid');
  if (!lastOk) problems.push('last_day_invalid');
  const year = (date: IsoDate) => Number(date.slice(0, 4));
  const inYears = (date: IsoDate) => year(date) >= MIN_YEAR && year(date) <= MAX_YEAR;
  if (firstOk && lastOk) {
    if (!inYears(calendar.firstDay) || !inYears(calendar.lastDay)) problems.push('calendar_out_of_range');
    else if (calendar.lastDay > `${year(calendar.firstDay) + MAX_SPAN_YEARS}${calendar.firstDay.slice(4)}`) problems.push('calendar_span_invalid');
    if (calendar.lastDay < calendar.firstDay) problems.push('last_day_before_first_day');
  }
  calendar.breaks.forEach((b, i) => {
    if (!isIsoDate(b.start) || !isIsoDate(b.end)) problems.push(`break_${i}_invalid`);
    else if (b.end < b.start) problems.push(`break_${i}_end_before_start`);
  });
  return problems;
}

const BREAK_NAME_MAX = 60;

/**
 * A calendar from untrusted input, keeping only the known fields, or null when its shape is
 * wrong. Date validity is `calendarProblems`' job.
 */
export function parseCalendar(value: unknown): Calendar | null {
  if (!value || typeof value !== 'object') return null;
  const { firstDay, lastDay, breaks } = value as Record<string, unknown>;
  if (typeof firstDay !== 'string' || typeof lastDay !== 'string' || !Array.isArray(breaks)) return null;
  const parsed: CalendarBreak[] = [];
  for (const b of breaks as unknown[]) {
    const { name, start, end, kind } = (b && typeof b === 'object' ? b : {}) as Record<string, unknown>;
    if (typeof name !== 'string' || typeof start !== 'string' || typeof end !== 'string' || (kind !== 'break' && kind !== 'testing')) return null;
    parsed.push({ name: name.trim().slice(0, BREAK_NAME_MAX), start, end, kind });
  }
  return { firstDay, lastDay, breaks: parsed };
}

/** Same first day, last day, and ordered breaks, after `parseCalendar` normalises both (plan Assumptions). */
export function sameCalendar(a: Calendar, b: Calendar): boolean {
  const [x, y] = [parseCalendar(a), parseCalendar(b)];
  return !!x && !!y && x.firstDay === y.firstDay && x.lastDay === y.lastDay && x.breaks.length === y.breaks.length
    && x.breaks.every((p, i) => { const q = y.breaks[i]!; return p.name === q.name && p.start === q.start && p.end === q.end && p.kind === q.kind; });
}

/** The school year a date belongs to: one that has not started by July belongs to the autumn before. */
export const schoolYearOf = (date: IsoDate): number => Number(date.slice(0, 4)) - (Number(date.slice(5, 7)) < 7 ? 1 : 0);

/** The school year a calendar is for: the one its first day falls in. */
export const calendarSchoolYear = (calendar: Calendar): number => schoolYearOf(calendar.firstDay);

export type CalendarValidation = { ok: true; calendar: Calendar } | { ok: false; problems: string[] };

/** A usable calendar from untrusted input: the right shape, valid dates, at least one teaching week. */
export function validateCalendar(value: unknown): CalendarValidation {
  const calendar = parseCalendar(value);
  if (!calendar) return { ok: false, problems: ['calendar_invalid'] };
  const problems = calendarProblems(calendar);
  if (problems.length) return { ok: false, problems };
  if (teachingWeeks(calendar).length === 0) return { ok: false, problems: ['no_teaching_weeks'] };
  return { ok: true, calendar };
}
